import classNames from 'classnames';
import type { ScaleOrdinal } from 'd3';
import { scaleBand, scaleLinear } from 'd3';
import { clamp } from 'lodash';
import type { JSX } from 'react';

import type { FormatType } from '../../types';
import { getFormatter, getTextWidth, truncateText } from '../../utilities';
import axisLabelStyles from '../axis/axis-label.module.css';
import type { BarData } from '../bar/bar-chart';
import { BarLabel } from '../bar/bar-label';
import styles from './grouped-bar-chart.module.css';
import {
  calculateGroupLayout,
  collectBarLabels,
  Dimensions,
  type DisplayBar,
  type DisplayGroup,
  GroupedBarLegend,
  getMaxBarCount,
  getZeroedExtent,
  HoverableBar,
} from './grouped-bar-shared';

type VerticalGroupedBarProps<TData extends object> = {
  displayData: DisplayGroup<TData>[];
  colorScale: ScaleOrdinal<string, string>;
  width: number;
  height: number;
  hoveredSeries: string | null;
  setHoveredSeries: (label: string | null) => void;
  format: FormatType;
  showBarLabels: boolean;
  hideLegend: boolean;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  tooltipContent?: (group: string, bar: BarData<TData>) => JSX.Element;
};

export const VerticalGroupedBar = <TData extends object>({
  displayData,
  colorScale,
  width,
  height,
  hoveredSeries,
  setHoveredSeries,
  format,
  showBarLabels,
  hideLegend,
  formatLegendLabel,
  maxLegendLabelWidth,
  tooltipContent,
}: VerticalGroupedBarProps<TData>) => {
  const barCountPerGroup = getMaxBarCount(displayData);
  const minPlotHeightHorizontalEquiv =
    displayData.length * Dimensions.MinBarHeight * barCountPerGroup;
  const minPlotHeight = Math.max(
    Dimensions.VerticalMinPlotHeightFloor,
    Math.min(minPlotHeightHorizontalEquiv, Dimensions.VerticalMinPlotHeightCap)
  );

  // Single bar per group makes legend redundant with axis labels.
  const shouldHideLegend =
    hideLegend ||
    barCountPerGroup <= 1 ||
    minPlotHeight + Dimensions.LegendHeight > height;
  const legendHeight = shouldHideLegend ? 0 : Dimensions.LegendHeight;

  const categoryLabelRotationRad =
    (Math.abs(Dimensions.VerticalCategoryLabelRotation) * Math.PI) / 180;

  const maxCategoryLinePx = displayData.reduce((longest, d) => {
    const w = getTextWidth({ text: d.group });
    return Math.max(longest, w ?? 0);
  }, 0);

  const truncatedCategoryWidth = Math.min(
    maxCategoryLinePx,
    Dimensions.VerticalCategoryTruncateCap
  );

  const verticalLeftBleed = clamp(
    10 + truncatedCategoryWidth * Math.cos(categoryLabelRotationRad) * 0.34,
    12,
    40
  );

  const verticalRightBleed = clamp(
    5 + truncatedCategoryWidth * Math.cos(categoryLabelRotationRad) * 0.06,
    6,
    14
  );

  const plotInnerWidth = Math.max(
    0,
    width -
      Dimensions.BarEdgePadding -
      verticalLeftBleed -
      Dimensions.BarEdgePadding -
      verticalRightBleed
  );

  const groupScale = scaleBand<string>()
    .domain(displayData.map((d) => d.group))
    .range([0, plotInnerWidth])
    .paddingInner(Dimensions.VerticalBandPaddingInner)
    .paddingOuter(Dimensions.VerticalBandPaddingOuter);

  const diagonalLabelAxisHeight = clamp(
    truncatedCategoryWidth * Math.sin(categoryLabelRotationRad) +
      Dimensions.VerticalApproxLineHeight * Math.cos(categoryLabelRotationRad) +
      18,
    Dimensions.VerticalAxisLabelHeightMin,
    Dimensions.VerticalAxisLabelHeightMax
  );

  const allCategoryLabelsFitHorizontal =
    displayData.length > 0 &&
    displayData.every((d) => {
      const textWidth = getTextWidth({ text: d.group });
      const bandWidth = groupScale.bandwidth();
      return (
        (textWidth ?? 0) <= bandWidth * Dimensions.VerticalCategoryBandFitRatio
      );
    });

  const labelAxisHeight = allCategoryLabelsFitHorizontal
    ? Dimensions.VerticalHorizontalCategoryStripPx
    : diagonalLabelAxisHeight;

  const plotHeight = Math.max(
    height - legendHeight - Dimensions.VerticalChartStackGap - labelAxisHeight,
    minPlotHeight
  );
  const chartInsetLeft = Dimensions.BarEdgePadding + verticalLeftBleed;

  const isPlotSizeValid = plotInnerWidth > 0 && plotHeight > 0;

  const barLabels = collectBarLabels(displayData);

  const barScale = scaleBand()
    .domain(barLabels)
    .range([0, groupScale.bandwidth()]);

  const zeroedExtent = getZeroedExtent(displayData);
  const valueScale = (() => {
    if (!zeroedExtent) return null;
    const labelReserve = Dimensions.VerticalBarLabelTopReserve;
    const topReserve = showBarLabels && zeroedExtent.max > 0 ? labelReserve : 0;
    const bottomReserve =
      showBarLabels && zeroedExtent.min < 0 ? labelReserve : 0;
    return scaleLinear(
      [zeroedExtent.min, zeroedExtent.max],
      [plotHeight - bottomReserve, topReserve]
    );
  })();

  const renderBar = (bar: DisplayBar<TData>, index: number, group: string) => {
    if (bar.value === null || !valueScale) return null;

    const barThickness = barScale.bandwidth();
    const barX = index * barScale.step();
    const y0 = valueScale(0);
    const y1 = valueScale(bar.value);
    const top = Math.min(y0, y1);
    const bottom = Math.max(y0, y1);
    const barPixelHeight = bottom - top;

    const labelRow = Dimensions.VerticalBarLabelRowHeight;
    const labelY = (() => {
      if (bar.value >= 0) {
        return Math.max(2, top - Dimensions.BarLabelPadding - labelRow);
      }
      return Math.min(
        plotHeight - labelRow - 2,
        bottom + Dimensions.BarLabelPadding
      );
    })();

    return (
      <HoverableBar
        key={bar.label}
        bar={bar}
        value={bar.value}
        group={group}
        format={format}
        fill={bar.color || colorScale(bar._colorKey)}
        rect={{ x: barX, y: top, width: barThickness, height: barPixelHeight }}
        hoveredSeries={hoveredSeries}
        setHoveredSeries={setHoveredSeries}
        tooltipContent={tooltipContent}
      >
        {showBarLabels && (
          <BarLabel
            textAnchor="middle"
            x={barX}
            y={labelY}
            width={barThickness}
            height={labelRow}
            label={getFormatter(format)(bar.value)}
            truncateMaxWidth={Math.min(
              Dimensions.VerticalValueLabelTruncateCap,
              Math.max(
                Dimensions.VerticalValueLabelTruncateFloorPx,
                groupScale.bandwidth() * 1.05
              )
            )}
          />
        )}
      </HoverableBar>
    );
  };

  const renderGroup = (group: DisplayGroup<TData>) => {
    const { primaryOffset } = calculateGroupLayout(
      group.values.length,
      barScale,
      groupScale.bandwidth()
    );
    const groupPos = groupScale(group.group);
    if (groupPos === undefined) return null;

    return (
      <g
        key={group.group}
        transform={`translate(${groupPos + primaryOffset}, 0)`}
        data-testid="grouped-bar-chart-group"
      >
        {group.values.map((bar, index) => renderBar(bar, index, group.group))}
      </g>
    );
  };

  const renderAxisLabel = (domain: string) => {
    const gx = groupScale(domain);
    if (gx === undefined) return null;

    const anchorX = gx + groupScale.bandwidth() / 2;

    if (allCategoryLabelsFitHorizontal) {
      return (
        <g key={domain} transform={`translate(${anchorX}, 4)`}>
          <text
            textAnchor="middle"
            dominantBaseline="hanging"
            fontSize={11}
            className={axisLabelStyles.yAxisLabel}
            data-testid="grouped-bar-chart-axis-label"
          >
            <title>{domain}</title>
            {domain}
          </text>
        </g>
      );
    }

    const categoryTruncateWidth = Math.min(
      Dimensions.VerticalCategoryTruncateCap,
      Math.max(
        Dimensions.VerticalCategoryTruncateFloorPx,
        groupScale.step() * 1.4
      )
    );

    const categoryText = truncateText({
      text: domain,
      maxWidth: categoryTruncateWidth,
    });

    return (
      <g key={domain} transform={`translate(${anchorX}, 8)`}>
        <text
          transform={`rotate(${Dimensions.VerticalCategoryLabelRotation})`}
          textAnchor="end"
          dominantBaseline="central"
          fontSize={11}
          className={axisLabelStyles.yAxisLabel}
          data-testid="grouped-bar-chart-axis-label"
        >
          <title>{domain}</title>
          {categoryText}
        </text>
      </g>
    );
  };

  const svgHeight = plotHeight + labelAxisHeight;

  return (
    <div
      style={{ width: '100%' }}
      className={classNames(
        styles.chartBodyVertical,
        styles.chartVerticalStack
      )}
    >
      <svg width="100%" height={svgHeight} overflow="visible">
        <g id="chart" transform={`translate(${chartInsetLeft}, 0)`}>
          {isPlotSizeValid && valueScale && displayData.map(renderGroup)}
        </g>
        <g
          id="x-axis"
          transform={`translate(${chartInsetLeft}, ${plotHeight})`}
        >
          {isPlotSizeValid && groupScale.domain().map(renderAxisLabel)}
        </g>
      </svg>
      {!shouldHideLegend && isPlotSizeValid && (
        <GroupedBarLegend
          barLabels={barLabels}
          displayData={displayData}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          belowSvg
        />
      )}
    </div>
  );
};
